/**
 * Figma Node IDs:
 * Design Frame: 1386:31323
 * Instance IDs: I945:24698, I1515:81987, I944:23233, I1515:81451, I944:23254, I944:23265, I945:23280
 * Composite: true
 * Last Updated: 2024-12-22
 * Generated by: Claude Code figma-component command
 */
import React, { useState } from "react";
import { CircleX, Hexagon } from "@labxchange/icons";
import { defineMessages, useIntl } from "react-intl";
import { RichText } from "@learningblocks/shared-components/lib/RichText";
import TranslationsProvider from "@learningblocks/simulations/shared-components/lib/TranslationsProvider";
import { LxButton } from "../LxButton";
import { fetchTranslations } from "./locales/config";
import {
  feedbackBannerBase,
  feedbackBannerVariants,
  feedbackBannerIconContainer,
  feedbackBannerMessage,
} from "./FeedbackBanner.css";

export type FeedbackBannerVariant =
  | "success"
  | "fail"
  | "warning"
  | "information";

/**
 * The banner's message, in any of the three forms it accepts: a plain string, a
 * React element rendered as-is, or a message descriptor resolved (and ICU
 * interpolated) by RichText.
 */
export type FeedbackBannerMessage =
  | string
  | React.ReactElement
  | {
      id: string;
      defaultMessage?: string;
      description?: string;
      placeholderValues?: Record<string, string | number>;
    };

/**
 * Type predicate over the exact message union: `React.isValidElement` alone
 * infers `ReactElement<any>`, which does not narrow the remaining arms, so the
 * string/descriptor branch would not typecheck against RichText.
 */
const isElementMessage = (
  message: FeedbackBannerMessage
): message is React.ReactElement => React.isValidElement(message);

export interface FeedbackBannerProps {
  /**
   * The variant/type of feedback banner
   */
  variant: FeedbackBannerVariant;

  /**
   * The main message to display - can be a string or a message descriptor.
   * `placeholderValues` is forwarded to RichText for ICU placeholder
   * interpolation (e.g. "{completed} of {total}").
   *
   * A React element is rendered as-is, for the case where the banner's text is
   * composed from more than one source — e.g. a translated verdict heading
   * followed by an already-translated body from content. Composing in the
   * caller keeps each piece a separate translatable string instead of requiring
   * a combined ICU message per pairing.
   */
  message: FeedbackBannerMessage;

  /**
   * Whether to show the close button
   * @default false
   */
  showCloseButton?: boolean;

  /**
   * Callback function called before the banner is destroyed
   */
  beforeClose?: () => void;

  /**
   * Additional CSS class name
   */
  className?: string;

  /**
   * Test ID for testing purposes
   */
  testId?: string;

  /**
   * ID for the banner element (useful for aria-describedby references)
   */
  id?: string;

  /**
   * Whether the banner renders with role="alert" so it is announced
   * automatically on insertion.
   * @default true
   * Set to `false` when rendering one banner per item (e.g. one per field) —
   * announcing several alerts at once competes for the screen reader's
   * attention and none of them reliably gets announced. In that case the
   * banner should instead be discoverable via `aria-describedby` /
   * normal navigation to the associated field.
   */
  alertRole?: boolean;
}

export const messages = defineMessages({
  closeBanner: {
    id: "FeedbackBanner.closeBanner",
    defaultMessage: "Close feedback banner",
    description: "Accessible label for close button",
  },
});

const FeedbackBannerInternal: React.FC<FeedbackBannerProps> = ({
  variant,
  message,
  showCloseButton = false,
  beforeClose,
  className,
  testId,
  id,
  alertRole = true,
}) => {
  const [isVisible, setIsVisible] = useState(true);

  // If component is not visible, don't render anything (self-destruction)
  if (!isVisible) {
    return null;
  }

  const handleClose = () => {
    // Call beforeClose callback if provided
    if (beforeClose) {
      beforeClose();
    }
    // Destroy the component
    setIsVisible(false);
  };

  //Check if the string contains MathJax syntax (e.g., $$...$$ or $...$)
  const containsMathJax = (text: string | undefined): boolean => {
    if (!text || typeof text !== "string") return false;
    return /\$\$.*?\$\$|\$.*?\$/g.test(text);
  };

  const bannerClasses = [
    feedbackBannerBase,
    feedbackBannerVariants[variant],
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const { formatMessage } = useIntl();

  return (
    <div
      className={bannerClasses}
      role={alertRole ? "alert" : undefined}
      data-testid={testId}
      data-variant={variant}
      id={id}
    >
      <div className={feedbackBannerIconContainer}>
        <Hexagon type={variant} />
      </div>
      <div className={feedbackBannerMessage}>
        {isElementMessage(message) ? (
          message
        ) : (
          <RichText
            value={message}
            addTagClasses={false}
            equation={containsMathJax(
              typeof message === "string" ? message : message.defaultMessage
            )}
          />
        )}
      </div>
      {showCloseButton && (
        <LxButton
          size="xs"
          shape="circle"
          variant="transparent"
          onClickHandler={handleClose}
          ariaLabel={formatMessage(messages.closeBanner)}
          Icon={CircleX}
        />
      )}
    </div>
  );
};

export const FeedbackBanner = (props: FeedbackBannerProps) => (
  <TranslationsProvider fetchTranslations={fetchTranslations}>
    <FeedbackBannerInternal {...props} />
  </TranslationsProvider>
);
